import React from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { iconFilters } from '../v3/utils/iconColorFilter';

// Импортируем Material Icons
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HotelRoundedIcon from '@mui/icons-material/HotelRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import MilitaryTechRoundedIcon from '@mui/icons-material/MilitaryTechRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// 1. Карта «строка» -> React-компонент иконки
const materialIconsMap = {
    arrowforward: <ArrowForwardRoundedIcon fontSize="inherit" />,
    sleep: <HotelRoundedIcon fontSize="inherit" />,
    people: <PeopleAltRoundedIcon fontSize="inherit" />,
    level: <MilitaryTechRoundedIcon fontSize="inherit" />,
    health: <FavoriteRoundedIcon fontSize="inherit" htmlColor="#FF4757" />,
    mana: <WaterDropRoundedIcon fontSize="inherit" htmlColor="#1E90FF" />,
};

// 2. Карта иконок характеристик из изображений
const statIconsMap = {
    attack: '/img/inventory.webp',
    attack_speed: '/img/speed.webp',
    critical_rate: '/img/flash.webp',
    critical_force: '/img/explosion.webp',
    accuracy: '/img/accuracy.webp',
    evasion: '/img/evasion.webp',
    armor: '/img/shield.webp',
    move_speed: '/img/sprint.webp',
    weight: '/img/weight.webp',
};

// Если не нашли в materialIconsMap, вставляем PNG
const IconImg = styled('img')(({ iconname, applyfilter }) => ({
    width: '1.5em',
    height: '1.5em',
    verticalAlign: 'middle',
    margin: '0 0.2em',
    // Apply primary color filter to steps, fatecoins and stat icons
    ...((iconname === 'steps' || iconname === 'fatecoins' || applyfilter) && {
        filter: iconFilters.primary,
    }),
}));

/**
 * Компонент-обёртка для картинки.
 * Если картинка не загрузилась (onError), возвращает null, чтобы не рендерить «кривой» контент.
 */
function SafeIcon({ iconName, src, applyFilter = false }) {
    const [error, setError] = React.useState(false);
    const [extension, setExtension] = React.useState('webp');
    
    // Если src передан явно, используем его
    if (src) {
        if (error) return null;
        
        return (
            <IconImg
                src={src}
                alt={iconName}
                iconname={iconName}
                applyfilter={applyFilter ? 1 : 0}
                onError={() => setError(true)}
            />
        );
    }
    
    // Иначе используем старую логику
    // Если расширение сброшено (т.е. не найден ни один вариант), возвращаем null
    if (!extension) return null;

    return (
        <IconImg
            src={`/img/${iconName}.${extension}`}
            alt={iconName}
            iconname={iconName}
            applyfilter={applyFilter ? 1 : 0}
            onError={() => {
                // Если не удалось загрузить .wp, пробуем .png
                if (extension === 'webp') {
                    setExtension('png');
                } else if (extension === 'png') {
                    // Если не удалось загрузить .png, сбрасываем расширение, чтобы вернуть null
                    setExtension(null);
                }
            }}
        />
    );
}
export default function RichText({ content = '' }) {
    // Глобальный счётчик для уникальных ключей
    let globalKeyIndex = 0;

    /**
     * Функция, которая в одном проходе обрабатывает:
     * - <br>, <b>, </b>
     * - и (самым последним) <nobr>, </nobr>
     */
    function renderHtmlChunk(text) {
        const result = [];
        let buffer = '';
        let isBold = false;
        let isNobr = false;

        for (let i = 0; i < text.length; i++) {
            // 1) <br> или <br/>
            const brMatch = text.slice(i).match(/^<br\s*\/?>/i);
            if (brMatch) {
                // Сбрасываем накопленное
                if (buffer) {
                    result.push(
                        isNobr ? (
                            <span
                                key={`nobr-${globalKeyIndex++}`}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {isBold ? <strong>{buffer}</strong> : buffer}
                            </span>
                        ) : (
                            isBold
                                ? <strong key={`b-${globalKeyIndex++}`}>{buffer}</strong>
                                : <React.Fragment key={`t-${globalKeyIndex++}`}>{buffer}</React.Fragment>
                        )
                    );
                    buffer = '';
                }
                // Добавляем перенос
                result.push(<br key={`br-${globalKeyIndex++}`} />);
                i += brMatch[0].length - 1;
                continue;
            }

            // 2) <b>
            if (text.slice(i).toLowerCase().startsWith('<b>')) {
                if (buffer) {
                    result.push(
                        isNobr ? (
                            <span
                                key={`nobr-${globalKeyIndex++}`}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {isBold ? <strong>{buffer}</strong> : buffer}
                            </span>
                        ) : (
                            isBold
                                ? <strong key={`b-${globalKeyIndex++}`}>{buffer}</strong>
                                : <React.Fragment key={`t-${globalKeyIndex++}`}>{buffer}</React.Fragment>
                        )
                    );
                    buffer = '';
                }
                isBold = true;
                i += 2; // пропускаем <b>
                continue;
            }

            // 3) </b>
            if (text.slice(i).toLowerCase().startsWith('</b>')) {
                if (buffer) {
                    result.push(
                        isNobr ? (
                            <span
                                key={`nobr-${globalKeyIndex++}`}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                <strong>{buffer}</strong>
                            </span>
                        ) : (
                            <strong key={`b-${globalKeyIndex++}`}>{buffer}</strong>
                        )
                    );
                    buffer = '';
                }
                isBold = false;
                i += 3; // пропускаем </b>
                continue;
            }

            // 4) <nobr> (обрабатываем после <b>, <br>, и т.д.)
            if (text.slice(i).toLowerCase().startsWith('<nobr>')) {
                // Сбрасываем накопленное (без nobr)
                if (buffer) {
                    result.push(
                        isBold
                            ? <strong key={`b-${globalKeyIndex++}`}>{buffer}</strong>
                            : <React.Fragment key={`t-${globalKeyIndex++}`}>{buffer}</React.Fragment>
                    );
                    buffer = '';
                }
                isNobr = true;
                i += 5; // пропускаем <nobr>
                continue;
            }

            // 5) </nobr>
            if (text.slice(i).toLowerCase().startsWith('</nobr>')) {
                if (buffer) {
                    // Сбрасываем накопленное как nowrap
                    result.push(
                        <span
                            key={`nobr-${globalKeyIndex++}`}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {isBold ? <strong>{buffer}</strong> : buffer}
                        </span>
                    );
                    buffer = '';
                }
                isNobr = false;
                i += 6; // пропускаем </nobr>
                continue;
            }

            // Обычный символ
            buffer += text[i];
        }

        // Хвост (после цикла)
        if (buffer) {
            if (isNobr) {
                result.push(
                    <span
                        key={`nobr-${globalKeyIndex++}`}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {isBold ? <strong>{buffer}</strong> : buffer}
                    </span>
                );
            } else {
                result.push(
                    isBold
                        ? <strong key={`b-${globalKeyIndex++}`}>{buffer}</strong>
                        : <React.Fragment key={`t-${globalKeyIndex++}`}>{buffer}</React.Fragment>
                );
            }
        }

        return result;
    }

    // --- Ниже идёт основной парсинг фигурных скобок { ... } ---
    const pattern = /\{([^}]+)\}/g;
    const result = [];
    let lastIndex = 0;
    let match;
    let mainKeyIndex = 0; // для вставок иконок

    while ((match = pattern.exec(content)) !== null) {
        // Текст ДО фигурных скобок
        if (match.index > lastIndex) {
            const chunk = content.slice(lastIndex, match.index);
            // Пропускаем chunk через renderHtmlChunk (b, br, nobr, etc.)
            result.push(...renderHtmlChunk(chunk));
        }

        // Само содержимое фигурных скобок
        const iconNameRaw = match[1];
        const iconName = iconNameRaw.toLowerCase().trim();

        // Проверяем в materialIconsMap
        if (materialIconsMap[iconName]) {
            // Рендерим Material Icon
            result.push(
                <Box
                    key={`mat-icon-${mainKeyIndex++}`}
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        fontSize: '1.5em',
                        verticalAlign: 'middle',
                        margin: '0 0.2em',
                    }}
                >
                    {materialIconsMap[iconName]}
                </Box>
            );
        } else if (statIconsMap[iconName]) {
            // Рендерим иконку характеристики с фильтром
            result.push(
                <SafeIcon
                    key={`stat-icon-${mainKeyIndex++}`}
                    iconName={iconName}
                    src={statIconsMap[iconName]}
                    applyFilter={true}
                />
            );
        } else {
            // Вместо прямой отрисовки картинки используем SafeIcon
            result.push(
                <SafeIcon
                    key={`img-icon-${mainKeyIndex++}`}
                    iconName={iconName}
                />
            );
        }

        lastIndex = pattern.lastIndex;
    }

    // Хвост (после последней фигурной скобки)
    if (lastIndex < content.length) {
        const chunk = content.slice(lastIndex);
        result.push(...renderHtmlChunk(chunk));
    }

    // Возвращаем весь массив с принудительным использованием JetBrains Mono
    return (
        <Box 
            component="span" 
            sx={{ 
                fontFamily: 'JetBrains Mono, monospace !important',
                '& *': {
                    fontFamily: 'inherit !important'
                }
            }}
        >
            {result}
        </Box>
    );
}